import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import useTranslations from "@/shared/lang/useTranslations";

import { pseBankNameFor, usePseBanks } from "../hooks/usePseBanks";
import {
  type PayinRuntimeEntity,
  PayinRuntimeStatus,
  PaymentsPayinService,
} from "../services/paymentsPayin.service";
import { loadLastRedirectPayinId, loadPseReturnContext } from "../utils/pseReturnContext";

const POLL_INTERVAL_MS = 5_000;
// The provider network resolves unfinished PSE flows on its own timeout — stop polling after ~15 min.
const POLL_MAX_ATTEMPTS = 180;

type Outcome = "waiting" | "confirmed" | "rejected" | "missing";

// The ingest composes the rejection reason as `provider_rejected:<network detail>` when it has one.
const REJECT_REASON_PREFIX = "provider_rejected:";

function networkDetailOf(payin: PayinRuntimeEntity | null): string | null {
  if (!payin) return null;
  if (payin.pse?.failure_detail) return payin.pse.failure_detail;
  if (payin.failure_reason?.startsWith(REJECT_REASON_PREFIX)) {
    return payin.failure_reason.slice(REJECT_REASON_PREFIX.length);
  }
  return null;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-right font-mono text-sm text-foreground">{value}</span>
    </div>
  );
}

/**
 * `/app/payins/result?payin=<id>` — where the payer lands after a bank-redirect flow (PSE, Botón
 * Bancolombia). This URL is the backend's `PSE_DEFAULT_RETURN_URL`: the PSE return callback 302s here
 * appending `?payin=<id>` (the callback itself already appended the network's `confirmed` proof server-side,
 * exactly once), while Botón Bancolombia lands verbatim and the id comes from the persisted redirect pointer.
 * The page only POLLS the payin until the provider webhook settles it — it never confirms anything itself.
 * The three outcomes render the PSE-mandated result screens: pending (CUS/bank/amount + the mandated notice),
 * rejected (CUS/bank/literal detail + retry), success (reference/amount/CUS/bank receipt). Bank name and
 * amount come from the context the deposit modal persisted right before redirecting; the API fields are the
 * fallback.
 */
const PayinReturnPage = () => {
  const { t, activeLanguage } = useTranslations("payins");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Both redirect rails arrive with ?payin=<id> (PSE via our callback, Botón Bancolombia baked into the
  // registered URL); the pointer the deposit modal persisted right before redirecting stays as the
  // same-tab fallback for a network that strips the query.
  const payinId = searchParams.get("payin") ?? loadLastRedirectPayinId();

  const [payin, setPayin] = useState<PayinRuntimeEntity | null>(null);
  const [outcome, setOutcome] = useState<Outcome>(payinId ? "waiting" : "missing");
  const attempts = useRef(0);

  const context = useMemo(() => (payinId ? loadPseReturnContext(payinId) : null), [payinId]);

  const breadcrumb = useMemo(
    () => [{ label: tGlobal("sidebar_section_billeteras") }, { label: t("return_breadcrumb") }],
    [t, tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  useEffect(() => {
    if (!payinId) return;
    let cancelled = false;

    const check = () => {
      attempts.current += 1;
      PaymentsPayinService.getPayin(payinId)
        .then((res) => {
          if (cancelled) return;
          setPayin(res.data);
          const status = res.data.status;
          if (status === PayinRuntimeStatus.CONFIRMED) {
            setOutcome("confirmed");
            clearInterval(timer);
          } else if (
            status === PayinRuntimeStatus.REJECTED ||
            status === PayinRuntimeStatus.FAILED ||
            status === PayinRuntimeStatus.REVERSED
          ) {
            setOutcome("rejected");
            clearInterval(timer);
          }
        })
        .catch(() => {
          // Transient errors (or a 404 while the session/org loads) — the next tick retries.
        });
      if (attempts.current > POLL_MAX_ATTEMPTS) clearInterval(timer);
    };

    const timer = setInterval(check, POLL_INTERVAL_MS);
    check();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [payinId]);

  // Bank name: the persisted context first (survives the bank round-trip); the directory as the fallback for
  // a payer who lands here without it (fresh tab, deep link). The directory is only requested when that
  // fallback is actually needed, and `usePseBanks` serves it from the same memo the deposit form filled.
  const needsDirectory = !context?.bankName && !!payin?.pse?.bank_code;
  const { banks } = usePseBanks(needsDirectory);
  const bankName = context?.bankName || pseBankNameFor(banks, payin?.pse?.bank_code ?? "");
  const grossAmount = context?.grossAmount ?? payin?.gross_amount_expected ?? null;
  const currency = context?.currency ?? payin?.currency ?? "COP";
  const amountLabel = grossAmount
    ? `${Math.round(Number(grossAmount)).toLocaleString(activeLanguage)} ${currency}`
    : null;
  const reference = context?.reference || (payinId ? payinId.slice(0, 8).toUpperCase() : "");
  const cus = payin?.pse?.trazability_code ?? null;
  // The success receipt prefers the core receipt id; the traceability code is the fallback.
  const receiptCus = payin?.pse?.core_id ?? cus;
  const detail = networkDetailOf(payin);
  const contactPhone = t("pse_contact_phone");
  const contactEmail = t("pse_contact_email");

  const infoCard = (rows: { label: string; value: string | null }[]) => {
    const visible = rows.filter((row): row is { label: string; value: string } => Boolean(row.value));
    if (visible.length === 0) return null;
    return (
      <div className="w-full max-w-sm divide-y divide-border/60 rounded-xl border border-border bg-muted/40 px-4 py-1.5 text-left">
        {visible.map((row) => (
          <InfoRow key={row.label} label={row.label} value={row.value} />
        ))}
      </div>
    );
  };

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center gap-5 px-4 py-16 text-center">
      {outcome === "missing" && (
        <>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <XCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="text-lg font-semibold text-foreground">{t("return_missing_title")}</p>
          <p className="text-sm text-muted-foreground">{t("return_missing_body")}</p>
        </>
      )}

      {outcome === "waiting" && (
        <>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
          <p className="text-lg font-semibold text-foreground">{t("return_pse_processing_title")}</p>
          {infoCard([
            { label: t("return_pse_info_cus"), value: cus },
            { label: t("return_pse_info_bank"), value: bankName || null },
            { label: t("return_pse_info_amount"), value: amountLabel },
          ])}
          <div className="w-full max-w-sm rounded-xl border border-amber-300/50 bg-amber-50 px-4 py-3 text-left text-sm text-amber-800 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-300">
            {t("return_pse_pending_callout", { phone: contactPhone, email: contactEmail })}
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">{t("return_pse_autoupdate")}</p>
        </>
      )}

      {outcome === "confirmed" && (
        <>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <p className="text-lg font-semibold text-primary">{t("return_pse_success_title")}</p>
          <p className="max-w-sm text-sm text-muted-foreground">{t("return_pse_success_body")}</p>
          {infoCard([
            { label: t("return_pse_info_reference"), value: reference || null },
            { label: t("return_pse_info_amount"), value: amountLabel },
            { label: t("return_pse_info_cus"), value: receiptCus },
            { label: t("return_pse_info_bank"), value: bankName || null },
          ])}
        </>
      )}

      {outcome === "rejected" && (
        <>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <XCircle className="h-8 w-8 text-destructive" />
          </div>
          <p className="text-lg font-semibold text-destructive">{t("return_rejected_title")}</p>
          <p className="max-w-sm text-sm text-muted-foreground">{t("return_pse_rejected_body")}</p>
          {infoCard([
            { label: t("return_pse_info_cus"), value: cus },
            { label: t("return_pse_info_bank"), value: bankName || null },
            { label: t("return_pse_info_detail"), value: detail },
          ])}
        </>
      )}

      <div className="flex gap-2 pt-2">
        {outcome === "confirmed" ? (
          <ColocaButton variant="primary" onClick={() => void navigate("/app/wallets")}>
            {t("return_pse_home")}
          </ColocaButton>
        ) : (
          <ColocaButton
            variant={outcome === "rejected" ? "ghost" : "primary"}
            onClick={() => void navigate("/app/wallets")}
          >
            {t("return_go_wallets")}
          </ColocaButton>
        )}
        {outcome === "rejected" && (
          <ColocaButton variant="primary" onClick={() => void navigate("/app/wallets")}>
            {t("return_pse_retry")}
          </ColocaButton>
        )}
      </div>
    </div>
  );
};

export default PayinReturnPage;
